/**
 * Scrollable message list for the agent drawer.
 *
 * Renders SDK-owned `AgentUIMessage`s part by part, IN PART ORDER — the
 * SDK appends resumed turns onto the same assistant message, so the parts
 * array is the chronology the clinician experienced. Text runs become
 * markdown bubbles, a fulfilled `list_structures` part becomes a "looked
 * up" chip, and each mutating tool part becomes its proposal card at its
 * own position — never grouped by type. Welcome screen when empty; a
 * "Thinking…" indicator while the model is working.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { Database } from 'lucide-react';
import { isTextUIPart, isToolUIPart, getToolName } from 'ai';
import {
    LIST_STRUCTURES_TOOL_NAME,
    APPLY_SYMMETRIC_MARGIN_TOOL_NAME,
    CREATE_RING_TOOL_NAME,
    APPLY_ASYMMETRIC_MARGIN_TOOL_NAME,
    BOOLEAN_OPERATION_TOOL_NAME,
    ANALYZE_IMAGE_FOR_MODEL_SELECTION_TOOL_NAME,
    PROPOSE_CONTOUR_MODEL_TOOL_NAME,
    START_AUTO_SEGMENTATION_TOOL_NAME,
    PROPOSE_LOAD_SERIES_TOOL_NAME,
    SEARCH_PATIENTS_TOOL_NAME,
    LIST_PATIENT_STUDIES_TOOL_NAME,
    LIST_IMPORTED_PATIENTS_TOOL_NAME,
    CHECK_PACS_CONNECTION_TOOL_NAME,
    FILL_CAVITIES_TOOL_NAME,
    REMOVE_SMALL_PARTS_TOOL_NAME,
    CLEAR_CONTOURS_TOOL_NAME,
    ZOOM_VIEW_TOOL_NAME,
    CHECK_AUTO_SEGMENTATION_JOBS_TOOL_NAME,
    CANCEL_AUTO_SEGMENTATION_TOOL_NAME,
    SEND_STRUCTURE_SET_TO_PACS_TOOL_NAME,
    SET_STRUCTURE_VISIBILITY_TOOL_NAME,
    GO_TO_POSITION_TOOL_NAME,
    SET_WINDOW_LEVEL_TOOL_NAME,
    SET_STRUCTURE_COLOR_TOOL_NAME,
    INTERPOLATE_STRUCTURE_TOOL_NAME,
    SMOOTH_STRUCTURE_TOOL_NAME,
    MOVE_STRUCTURE_TOOL_NAME,
    RENAME_STRUCTURE_TOOL_NAME,
    DELETE_STRUCTURE_TOOL_NAME,
    DUPLICATE_STRUCTURE_TOOL_NAME,
    REPLACE_STRUCTURE_CONTOURS_TOOL_NAME,
    SET_STRUCTURE_TYPE_TOOL_NAME,
    CREATE_MARKER_TOOL_NAME,
    SET_MARKER_POSITION_TOOL_NAME,
    CREATE_STRUCTURE_SET_TOOL_NAME,
    EDIT_STRUCTURE_SET_INFO_TOOL_NAME,
    SET_STRUCTURE_SET_APPROVAL_TOOL_NAME,
    GET_STRUCTURE_PROPERTIES_TOOL_NAME,
    RESET_DISPLAY_TOOL_NAME,
    SHOW_IN_VIEWER_TOOL_NAME,
    LIST_TIMEPOINTS_TOOL_NAME,
    BRING_PRIOR_CONTOUR_TOOL_NAME,
    COMPARE_STRUCTURES_TOOL_NAME,
    type AgentUIMessage,
    type ProposalChannelView,
    type ProposalsByTool,
    type MarginProposal,
    type RingProposal,
    type AsymMarginProposal,
    type BooleanProposal,
    type ModelProposal,
    type LoadSeriesProposal,
    type FillCavitiesProposal,
    type RemoveSmallPartsProposal,
    type ClearContoursProposal,
    type CancelJobProposal,
    type SendStructureSetProposal,
    type ColorProposal,
    type InterpolateProposal,
    type SmoothProposal,
    type MoveProposal,
    type ChangeProposal,
} from '~/agent';
import ChatMessage from './ChatMessage';
import MarginProposalCard from './MarginProposalCard';
import RingProposalCard from './RingProposalCard';
import AsymmetricMarginProposalCard from './AsymmetricMarginProposalCard';
import BooleanProposalCard from './BooleanProposalCard';
import ContourModelProposalCard from './ContourModelProposalCard';
import LoadSeriesProposalCard from './LoadSeriesProposalCard';
import FillCavitiesProposalCard from './FillCavitiesProposalCard';
import RemoveSmallPartsProposalCard from './RemoveSmallPartsProposalCard';
import ClearContoursProposalCard from './ClearContoursProposalCard';
import CancelAutoSegmentationProposalCard from './CancelAutoSegmentationProposalCard';
import SendStructureSetProposalCard from './SendStructureSetProposalCard';
import StructureColorProposalCard from './StructureColorProposalCard';
import InterpolateProposalCard from './InterpolateProposalCard';
import SmoothProposalCard from './SmoothProposalCard';
import MoveStructureProposalCard from './MoveStructureProposalCard';
import StructureChangeProposalCard from './StructureChangeProposalCard';

interface ChatMessageListProps {
    messages: AgentUIMessage[];
    busy: boolean;
    /** Card views keyed by tool name, then by toolCallId. */
    proposalsByTool: ProposalsByTool;
    onApply: (toolName: string, toolCallId: string) => void;
    onReject: (toolName: string, toolCallId: string) => void;
    /** Show/clear a translucent overlay of the proposal while its card is hovered. */
    onPreviewEnter: (toolName: string, toolCallId: string) => void;
    onPreviewLeave: (toolName: string, toolCallId: string) => void;
}

/** The gate callbacks a card binds, already closed over its toolCallId. */
interface CardHandlers {
    onApply: () => void;
    onReject: () => void;
    onPreviewEnter: () => void;
    onPreviewLeave: () => void;
}

type CardRenderer = (
    toolCallId: string,
    view: ProposalChannelView<unknown>,
    handlers: CardHandlers
) => ReactNode;

// One card per mutating tool. The `proposal` cast is sound: a view comes from
// `proposalsByTool[thisToolName]`, whose proposals are this op's type. Adding an
// op is a single entry here — no new branch in the render path.
const renderModelCard: CardRenderer = (id, view, handlers) => {
    const p = view.proposal as ModelProposal;
    return (
        <ContourModelProposalCard
            key={id}
            action={p.action}
            modelLabel={p.modelLabel}
            modelName={p.modelName}
            bodyPart={p.bodyPart}
            customizationName={p.customizationName}
            status={view.status}
            failureReason={view.failureReason}
            {...handlers}
        />
    );
};

// One card for every structure-management tool (shared proposal shape).
const renderChangeCard: CardRenderer = (id, view, handlers) => {
    const p = view.proposal as ChangeProposal<unknown>;
    return (
        <StructureChangeProposalCard
            key={id}
            title={p.title}
            summary={p.summary}
            details={p.details}
            undoable={p.undoable}
            caution={p.caution}
            status={view.status}
            failureReason={view.failureReason}
            {...handlers}
        />
    );
};

// Chip label for the auto-run tools with no card: the read-only PACS
// patient tools and the view-only zoom.
const PATIENT_READ_CHIP: Record<string, string> = {
    [SEARCH_PATIENTS_TOOL_NAME]: 'Searched PACS patients',
    [LIST_PATIENT_STUDIES_TOOL_NAME]: 'Listed patient studies',
    [LIST_IMPORTED_PATIENTS_TOOL_NAME]: 'Checked imported patients',
    [CHECK_PACS_CONNECTION_TOOL_NAME]: 'Checked PACS connection',
    [ZOOM_VIEW_TOOL_NAME]: 'Adjusted the view',
    [CHECK_AUTO_SEGMENTATION_JOBS_TOOL_NAME]: 'Checked auto-segmentation jobs',
    [SET_STRUCTURE_VISIBILITY_TOOL_NAME]: 'Changed structure visibility',
    [GO_TO_POSITION_TOOL_NAME]: 'Moved to a position',
    [SET_WINDOW_LEVEL_TOOL_NAME]: 'Changed window/level',
    [GET_STRUCTURE_PROPERTIES_TOOL_NAME]: 'Read structure properties',
    [RESET_DISPLAY_TOOL_NAME]: 'Reset the display',
    [SHOW_IN_VIEWER_TOOL_NAME]: 'Highlighted in the viewer',
    [LIST_TIMEPOINTS_TOOL_NAME]: 'Listed the patient’s scans',
    [COMPARE_STRUCTURES_TOOL_NAME]: 'Compared the structures',
};

const CARD_BY_TOOL: Record<string, CardRenderer> = {
    [SET_STRUCTURE_COLOR_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as ColorProposal;
        return (
            <StructureColorProposalCard
                key={id}
                targetName={p.target.name}
                targetRoiNumber={p.target.roiNumber}
                fromColor={p.fromColor}
                toColor={p.toColor}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [CANCEL_AUTO_SEGMENTATION_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as CancelJobProposal;
        return (
            <CancelAutoSegmentationProposalCard
                key={id}
                model={p.model}
                jobStatus={p.status}
                startedAt={p.startedAt}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [SEND_STRUCTURE_SET_TO_PACS_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as SendStructureSetProposal;
        return (
            <SendStructureSetProposalCard
                key={id}
                structureSetLabel={p.structureSetLabel}
                targetName={p.targetName}
                structureNames={p.structureNames}
                isDefaultSelection={p.isDefaultSelection}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [FILL_CAVITIES_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as FillCavitiesProposal;
        return (
            <FillCavitiesProposalCard
                key={id}
                targetName={p.target.name}
                targetRoiNumber={p.target.roiNumber}
                currentVolumeCm3={p.target.currentVolumeCm3}
                volumeDeltaCm3={p.volumeDeltaCm3}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [REMOVE_SMALL_PARTS_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as RemoveSmallPartsProposal;
        return (
            <RemoveSmallPartsProposalCard
                key={id}
                targetName={p.target.name}
                targetRoiNumber={p.target.roiNumber}
                currentVolumeCm3={p.target.currentVolumeCm3}
                minVolumeCm3={p.minVolumeCm3}
                partsFound={p.partsFound}
                partsRemoved={p.partsRemoved}
                volumeDeltaCm3={p.volumeDeltaCm3}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [CLEAR_CONTOURS_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as ClearContoursProposal;
        return (
            <ClearContoursProposalCard
                key={id}
                targetName={p.target.name}
                targetRoiNumber={p.target.roiNumber}
                currentVolumeCm3={p.target.currentVolumeCm3}
                scope={p.scope}
                keepEveryNth={p.keepEveryNth}
                slicePositionMm={p.slicePositionMm}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [INTERPOLATE_STRUCTURE_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as InterpolateProposal;
        return (
            <InterpolateProposalCard
                key={id}
                targetName={p.target.name}
                targetRoiNumber={p.target.roiNumber}
                currentVolumeCm3={p.target.currentVolumeCm3}
                scope={p.scope}
                slicesToFill={p.slicesToFill}
                slicePositionMm={p.slicePositionMm}
                currentSliceFill={p.currentSliceFill}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [SMOOTH_STRUCTURE_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as SmoothProposal;
        return (
            <SmoothProposalCard
                key={id}
                targetName={p.target.name}
                targetRoiNumber={p.target.roiNumber}
                currentVolumeCm3={p.target.currentVolumeCm3}
                method={p.method}
                level={p.level}
                volumeDeltaCm3={p.volumeDeltaCm3}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [MOVE_STRUCTURE_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as MoveProposal;
        return (
            <MoveStructureProposalCard
                key={id}
                targetName={p.target.name}
                targetRoiNumber={p.target.roiNumber}
                currentVolumeCm3={p.target.currentVolumeCm3}
                direction={p.direction}
                distanceCm={p.distanceCm}
                requestedDistanceCm={p.requestedDistanceCm}
                currentCentreCm={p.currentCentreCm}
                newCentreCm={p.newCentreCm}
                roomLeftCm={p.roomLeftCm}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [RENAME_STRUCTURE_TOOL_NAME]: renderChangeCard,
    [DELETE_STRUCTURE_TOOL_NAME]: renderChangeCard,
    [DUPLICATE_STRUCTURE_TOOL_NAME]: renderChangeCard,
    [REPLACE_STRUCTURE_CONTOURS_TOOL_NAME]: renderChangeCard,
    [SET_STRUCTURE_TYPE_TOOL_NAME]: renderChangeCard,
    [CREATE_MARKER_TOOL_NAME]: renderChangeCard,
    [SET_MARKER_POSITION_TOOL_NAME]: renderChangeCard,
    [CREATE_STRUCTURE_SET_TOOL_NAME]: renderChangeCard,
    [EDIT_STRUCTURE_SET_INFO_TOOL_NAME]: renderChangeCard,
    [SET_STRUCTURE_SET_APPROVAL_TOOL_NAME]: renderChangeCard,
    [BRING_PRIOR_CONTOUR_TOOL_NAME]: renderChangeCard,
    [PROPOSE_LOAD_SERIES_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as LoadSeriesProposal;
        return (
            <LoadSeriesProposalCard
                key={id}
                patientId={p.patientId}
                patientName={p.patientName}
                targetName={p.targetName}
                studyDescription={p.studyDescription}
                studyDate={p.studyDate}
                series={p.series}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [PROPOSE_CONTOUR_MODEL_TOOL_NAME]: renderModelCard,
    [START_AUTO_SEGMENTATION_TOOL_NAME]: renderModelCard,
    [APPLY_SYMMETRIC_MARGIN_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as MarginProposal;
        return (
            <MarginProposalCard
                key={id}
                targetName={p.target.name}
                targetRoiNumber={p.target.roiNumber}
                currentVolumeCm3={p.target.currentVolumeCm3}
                marginCm={p.marginCm}
                requestedMarginCm={p.requestedMarginCm}
                newRoi={p.newRoi}
                volumeDeltaCm3={p.volumeDeltaCm3}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [CREATE_RING_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as RingProposal;
        return (
            <RingProposalCard
                key={id}
                targetName={p.target.name}
                targetRoiNumber={p.target.roiNumber}
                currentVolumeCm3={p.target.currentVolumeCm3}
                innerCm={p.innerCm}
                outerCm={p.outerCm}
                requestedInnerCm={p.requestedInnerCm}
                requestedOuterCm={p.requestedOuterCm}
                newRoi={p.newRoi}
                ringVolumeCm3={p.ringVolumeCm3}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [APPLY_ASYMMETRIC_MARGIN_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as AsymMarginProposal;
        return (
            <AsymmetricMarginProposalCard
                key={id}
                targetName={p.target.name}
                targetRoiNumber={p.target.roiNumber}
                currentVolumeCm3={p.target.currentVolumeCm3}
                superiorCm={p.superiorCm}
                inferiorCm={p.inferiorCm}
                anteriorCm={p.anteriorCm}
                posteriorCm={p.posteriorCm}
                leftCm={p.leftCm}
                rightCm={p.rightCm}
                roundedDirections={p.roundedDirections}
                newRoi={p.newRoi}
                volumeDeltaCm3={p.volumeDeltaCm3}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
    [BOOLEAN_OPERATION_TOOL_NAME]: (id, view, handlers) => {
        const p = view.proposal as BooleanProposal;
        return (
            <BooleanProposalCard
                key={id}
                steps={p.steps}
                newRoi={p.newRoi}
                compositeVolumeCm3={p.compositeVolumeCm3}
                volumeDeltaCm3={p.volumeDeltaCm3}
                status={view.status}
                failureReason={view.failureReason}
                {...handlers}
            />
        );
    },
};

export default function ChatMessageList({
    messages,
    busy,
    proposalsByTool,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: ChatMessageListProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = containerRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages, busy]);

    if (messages.length === 0 && !busy) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                flexGrow={1}
                px={2.5}
                py={4}
                gap={0.5}
            >
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    textAlign="center"
                >
                    MVision Agent
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    fontSize="0.8rem"
                >
                    Structure operations, powered by AI
                </Typography>
            </Box>
        );
    }

    const cardFor = (toolName: string, toolCallId: string): ReactNode => {
        const view = proposalsByTool[toolName]?.[toolCallId];
        if (!view) {
            return null;
        }
        const renderCard = CARD_BY_TOOL[toolName];
        if (!renderCard) {
            return null;
        }
        return renderCard(toolCallId, view, {
            onApply: () => onApply(toolName, toolCallId),
            onReject: () => onReject(toolName, toolCallId),
            onPreviewEnter: () => onPreviewEnter(toolName, toolCallId),
            onPreviewLeave: () => onPreviewLeave(toolName, toolCallId),
        });
    };

    const messageNodes = (msg: AgentUIMessage): ReactNode[] => {
        const nodes: ReactNode[] = [];
        const role = msg.role === 'user' ? 'user' : 'assistant';
        let textRun = '';
        let textRunIndex = 0;
        const flushText = () => {
            if (textRun.length > 0) {
                nodes.push(
                    <ChatMessage
                        key={`${msg.id}-text-${textRunIndex}`}
                        role={role}
                        content={textRun}
                    />
                );
                textRunIndex += 1;
                textRun = '';
            }
        };

        for (const part of msg.parts) {
            if (isTextUIPart(part)) {
                textRun += part.text;
                continue;
            }
            if (!isToolUIPart(part)) {
                continue;
            }
            // A tool call ends the current reasoning turn: flush now so the
            // narration before and after it stay separate bubbles, even when
            // the tool renders nothing (e.g. a recoverable failure with no card).
            flushText();
            const toolName = getToolName(part);
            if (toolName === LIST_STRUCTURES_TOOL_NAME) {
                if (part.state === 'output-available') {
                    const count = Array.isArray(part.output)
                        ? part.output.length
                        : 0;
                    nodes.push(
                        <Box
                            key={`${msg.id}-read-${part.toolCallId}`}
                            display="flex"
                            mb={1}
                        >
                            <Chip
                                icon={<Database size={10} />}
                                label={`Looked up ${count} structures`}
                                size="small"
                                variant="outlined"
                                sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    '& .MuiChip-icon': { ml: 0.5 },
                                }}
                            />
                        </Box>
                    );
                }
                continue;
            }
            if (toolName === ANALYZE_IMAGE_FOR_MODEL_SELECTION_TOOL_NAME) {
                if (part.state === 'output-available') {
                    nodes.push(
                        <Box
                            key={`${msg.id}-analyze-${part.toolCallId}`}
                            display="flex"
                            mb={1}
                        >
                            <Chip
                                icon={<Database size={10} />}
                                label="Analysed image for Contour+ models"
                                size="small"
                                variant="outlined"
                                sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    '& .MuiChip-icon': { ml: 0.5 },
                                }}
                            />
                        </Box>
                    );
                }
                continue;
            }
            const patientChip = PATIENT_READ_CHIP[toolName];
            if (patientChip) {
                if (part.state === 'output-available') {
                    nodes.push(
                        <Box
                            key={`${msg.id}-pacs-${part.toolCallId}`}
                            display="flex"
                            mb={1}
                        >
                            <Chip
                                icon={<Database size={10} />}
                                label={patientChip}
                                size="small"
                                variant="outlined"
                                sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    '& .MuiChip-icon': { ml: 0.5 },
                                }}
                            />
                        </Box>
                    );
                }
                continue;
            }
            const card = cardFor(toolName, part.toolCallId);
            if (card !== null) {
                nodes.push(card);
            }
        }
        flushText();
        return nodes;
    };

    return (
        <Box ref={containerRef} flexGrow={1} overflow="auto" px={1.5} py={1}>
            {messages.map((msg) => (
                <Box key={msg.id}>{messageNodes(msg)}</Box>
            ))}

            {busy && (
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={1.5}
                    sx={(t) => ({
                        px: 1.5,
                        py: 1,
                        borderRadius: '12px',
                        borderBottomLeftRadius: '4px',
                        backgroundColor:
                            t.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.06)'
                                : 'rgba(0,0,0,0.04)',
                        border: `1px solid ${t.palette.divider}`,
                        width: 'fit-content',
                    })}
                >
                    <CircularProgress size={14} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize="0.8rem"
                    >
                        Thinking...
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
